import { useEffect, useState } from 'react';
import { engineFetchJson } from '@/lib/engineHttp';
import type { WidgetDefinition, WidgetProps } from '@/types/widget';

interface ProviderSpend {
  provider_id: string;
  display_name: string;
  today_cost_usd: number;
  week_cost_usd: number;
  month_cost_usd: number;
  month_call_count: number;
}

interface CostData {
  totalToday: number;
  totalMonth: number;
  budgetLimit: number;
  projectedMonthly: number;
  providers: ProviderSpend[];
  dailyTrends: { date: string; total_cost_usd: number }[];
}

/** Tiny inline sparkline (pure CSS bar chart). */
function Sparkline({ values }: { values: number[] }) {
  if (values.length === 0) return null;
  const max = Math.max(...values, 0.001);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 24 }}>
      {values.slice(-14).map((v, i) => (
        <div
          key={i}
          style={{
            width: 4,
            height: `${Math.max(2, (v / max) * 100)}%`,
            background: 'var(--sp-accent)',
            borderRadius: 1,
            opacity: i === values.length - 1 ? 1 : 0.5,
          }}
        />
      ))}
    </div>
  );
}

function CostMeterWidget({ context }: WidgetProps) {
  const [data, setData] = useState<CostData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.allSettled([
      engineFetchJson<{
        provider_spend?: ProviderSpend[];
        projected_monthly_cost_usd?: number;
      }>('/settings/llm/costs'),
      engineFetchJson<{
        total_cost_usd?: number;
        budget_limit_usd?: number;
        daily_trends?: CostData['dailyTrends'];
      }>('/costs/summary?days=30'),
    ]).then(([llmResult, generalResult]) => {
      const llm = llmResult.status === 'fulfilled' ? llmResult.value : null;
      const general = generalResult.status === 'fulfilled' ? generalResult.value : null;

      if (!llm && !general) {
        setError('Cost endpoints unavailable');
        return;
      }

      const providers: ProviderSpend[] = llm?.provider_spend ?? [];
      const totalToday = providers.reduce((s: number, p: ProviderSpend) => s + p.today_cost_usd, 0);
      const totalMonth = general?.total_cost_usd ?? providers.reduce((s: number, p: ProviderSpend) => s + p.month_cost_usd, 0);
      const budgetLimit = general?.budget_limit_usd ?? 0;
      const projectedMonthly = llm?.projected_monthly_cost_usd ?? 0;
      const dailyTrends = general?.daily_trends ?? [];

      setData({ totalToday, totalMonth, budgetLimit, projectedMonthly, providers, dailyTrends });
      setError(null);
    });
  }, [context.authToken]);

  return (
    <div className="sp-widget">
      <div className="sp-widget-header">
        <span>Cost Meter</span>
        <a href="/settings/costs" style={{ fontSize: 11, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
          Details
        </a>
      </div>
      <div className="sp-widget-body" style={{ padding: '4px 12px' }}>
        {error ? (
          <div>
            <div style={{ color: 'var(--sp-warn)', fontSize: 12, marginBottom: 4 }}>
              Awaiting engine endpoint
            </div>
            <div className="mono" style={{ color: 'var(--sp-text-dim)', fontSize: 11 }}>
              GET /api/v1/settings/llm/costs
            </div>
            <div className="mono" style={{ color: 'var(--sp-text-dim)', fontSize: 11 }}>
              GET /api/v1/costs/summary
            </div>
            {/* Placeholder sparkline */}
            <div style={{ marginTop: 12 }}>
              <Sparkline values={[0.3, 0.5, 0.2, 0.8, 0.4, 0.6, 0.7, 0.9, 0.3, 0.5, 0.4, 0.6, 0.8, 0.5]} />
              <div style={{ fontSize: 10, color: 'var(--sp-text-dim)', marginTop: 2 }}>14-day trend (mock)</div>
            </div>
          </div>
        ) : data ? (
          <div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 22, fontWeight: 700 }}>${data.totalToday.toFixed(2)}</span>
              <span style={{ fontSize: 12, color: 'var(--sp-text-muted)' }}>today</span>
              {data.budgetLimit > 0 && (
                <span style={{ fontSize: 11, color: data.totalMonth > data.budgetLimit * 0.8 ? 'var(--sp-warn)' : 'var(--sp-text-dim)' }}>
                  ${data.totalMonth.toFixed(2)} / ${data.budgetLimit.toFixed(0)} budget
                </span>
              )}
            </div>

            {data.dailyTrends.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <Sparkline values={data.dailyTrends.map((d) => d.total_cost_usd)} />
              </div>
            )}

            {data.providers.length > 0 && (
              <div style={{ borderTop: '1px solid var(--sp-border)', paddingTop: 6 }}>
                {data.providers.slice(0, 5).map((p) => (
                  <div key={p.provider_id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '2px 0' }}>
                    <span style={{ color: 'var(--sp-text-muted)' }}>{p.display_name}</span>
                    <span className="mono">${p.today_cost_usd.toFixed(3)}</span>
                  </div>
                ))}
              </div>
            )}

            {data.projectedMonthly > 0 && (
              <div style={{ fontSize: 11, color: 'var(--sp-text-dim)', marginTop: 4 }}>
                Projected: ${data.projectedMonthly.toFixed(2)}/mo
              </div>
            )}
          </div>
        ) : (
          <div style={{ color: 'var(--sp-text-muted)', fontSize: 12 }}>Loading costs...</div>
        )}
      </div>
    </div>
  );
}

export const costMeterWidget: WidgetDefinition = {
  id: 'cost-meter',
  name: 'Cost Meter',
  description: "Today's LLM spend by provider with sparkline trend.",
  icon: 'dollar-sign',
  category: 'cost',
  scope: 'both',
  defaultSize: { w: 6, h: 4 },
  minSize: { w: 3, h: 3 },
  component: CostMeterWidget,
};
